import {
  type MutableRefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

import { useConversationScope } from './useConversationScope';
import type { ActiveTransportExecution } from './useConversationTransport.types';

type UseConversationTransportScopeParams = {
  activeConversationId: string | null;
  clearTransportState: () => void;
  conversationScopeVersion: number;
  currentTransportExecutionRef: MutableRefObject<ActiveTransportExecution | null>;
};

/**
 * Invalidates transport work before a changed conversation can paint.
 * @param params - Active conversation and transport cleanup callback
 * @returns Scope controls for individual transport executions
 */
export const useConversationTransportScope = ({
  activeConversationId,
  clearTransportState,
  conversationScopeVersion,
  currentTransportExecutionRef,
}: UseConversationTransportScopeParams) => {
  const { captureScope, isScopeCurrent } = useConversationScope(
    activeConversationId,
    conversationScopeVersion
  );
  const previousScopeRef = useRef({
    conversationId: activeConversationId,
    version: conversationScopeVersion,
  });

  const isExecutionCurrent = useCallback(
    (execution: ActiveTransportExecution): boolean =>
      isScopeCurrent(execution.scope, execution.turn.conversationId),
    [isScopeCurrent]
  );

  useLayoutEffect(() => {
    const previousScope = previousScopeRef.current;
    previousScopeRef.current = {
      conversationId: activeConversationId,
      version: conversationScopeVersion,
    };
    const execution = currentTransportExecutionRef.current;
    const isCreatedConversationHandoff =
      previousScope.version === conversationScopeVersion &&
      previousScope.conversationId === null &&
      activeConversationId !== null &&
      execution !== null &&
      execution.turn.conversationId === activeConversationId &&
      isExecutionCurrent(execution);
    if (
      (previousScope.conversationId !== activeConversationId ||
        previousScope.version !== conversationScopeVersion) &&
      !isCreatedConversationHandoff
    ) {
      clearTransportState();
    }
  }, [
    activeConversationId,
    clearTransportState,
    conversationScopeVersion,
    currentTransportExecutionRef,
    isExecutionCurrent,
  ]);

  useLayoutEffect(() => () => clearTransportState(), [clearTransportState]);

  return { captureScope, isExecutionCurrent };
};
